import React, { useEffect, useRef } from 'react';

import { Modal, Input, Typography, message, Tag, Space, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useSerialNumberInput } from '@/hooks/useSerialNumberInput';

const { Text } = Typography;

interface SerialNumberModalProduct {
  id?: string;
  name?: string | null;
  sku?: string | null;
  barcode?: string | null;
  required_serial_numbers_count?: number;
  serial_number_pattern?: string | null;
  box_serial_number_delimiter?: string | null;
}

interface SerialNumberModalItem {
  quantity?: number;
  shipped_quantity?: number;
  product?: SerialNumberModalProduct;
}

interface SerialNumberModalProps {
  open: boolean;
  item?: SerialNumberModalItem | null;
  scanQuantity?: number;
  existingSerialNumbers?: string[];
  onConfirm: (serials: string[]) => void;
  onCancel: () => void;
}

const SerialNumbersContainer = styled.div`
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SerialTag = styled(Tag)`
  font-size: 14px;
  padding: 6px 12px;
  margin: 4px;
  font-family: monospace;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CountInfo = styled.div<{ completed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.completed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(14, 116, 144, 0.1)'};
  border-radius: 6px;
  margin-bottom: 12px;
`;

const CountLabel = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
`;

const CountValue = styled(Text)<{ completed: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.completed ? '#22c55e' : '#0e7490'};
`;

export default function SerialNumberModal({
  open,
  item,
  scanQuantity = 1,
  existingSerialNumbers = [],
  onConfirm,
  onCancel,
}: SerialNumberModalProps) {
  const { t } = useTranslation();

  const requiredPerUnit = item?.product?.required_serial_numbers_count || 1;
  // 基準件數：本次掃描隱含的件數（中/大單位條碼會 > 1）。也是「最少」要刷的件數。
  const baseUnits = Math.max(1, scanQuantity || 1);
  // 剩餘可出貨件數 = 該品項訂單量 − 已刷件數。同時也是這次能刷的上限。
  const remainingUnits = Math.max(0, (item?.quantity || 0) - (item?.shipped_quantity || 0));
  const baseRequiredCount = baseUnits * requiredPerUnit;
  const maxAllowedSerials = remainingUnits * requiredPerUnit;

  // 持有 auto-confirm 延遲計時器的 id，方便 modal 關閉 / unmount 時清掉，
  // 避免使用者在 300ms 窗口內按取消／關閉後 onConfirm 還是被觸發，
  // 對已 unmount 的 parent 做 stale state update。
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    input: currentInput,
    setInput: setCurrentInput,
    scannedSerials: serialNumbers,
    error: serialError,
    submit: submitSerial,
    removeAt: removeSerialAt,
    reset: resetSerials,
    inputRef: serialInputRef,
    isCompleted,
  } = useSerialNumberInput({
    productFlags: {
      required_serial_numbers_count: item?.product?.required_serial_numbers_count,
      serial_number_pattern: item?.product?.serial_number_pattern,
      box_serial_number_delimiter: item?.product?.box_serial_number_delimiter,
      product_barcode: item?.product?.barcode,
    },
    scanQuantity: baseUnits,
    maxQuantity: remainingUnits,
    existingSerials: existingSerialNumbers,
    onCompleted: (serials) => {
      // 延遲一下讓使用者看到序號被加入後再 auto-confirm。
      // 用 ref 持有 timer id，這樣 modal 在 300ms 內關閉 / unmount 時，
      // 下方的 useEffect 可以 clearTimeout 避免 onConfirm 還是被觸發。
      if (confirmTimerRef.current !== null) {
        clearTimeout(confirmTimerRef.current);
      }
      confirmTimerRef.current = setTimeout(() => {
        confirmTimerRef.current = null;
        onConfirm(serials);
      }, 300);
    },
  });

  const currentCount = serialNumbers.length;
  const completedUnits = requiredPerUnit > 0 && currentCount % requiredPerUnit === 0
    ? currentCount / requiredPerUnit
    : 0;

  // 當 Modal 打開時重置序號。focus 由 useSerialNumberInput hook 在 mount 時處理。
  useEffect(() => {
    if (open) {
      resetSerials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 關閉 (open=false) 或 unmount 時清掉還沒觸發的 auto-confirm timer，
  // 避免在父層已經關閉 modal 後 onConfirm 還是被觸發、對 stale state 做 update。
  useEffect(() => {
    if (!open && confirmTimerRef.current !== null) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    return () => {
      if (confirmTimerRef.current !== null) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    };
  }, [open]);

  const handleConfirm = () => {
    if (!isCompleted) {
      message.error(t('operator.qc.serial_number_modal.serial_count_mismatch', {
        defaultValue: '序號數量需要是每件 {{per_unit}} 個的整數倍，且至少 {{min}} 個（目前 {{actual}} 個）',
        per_unit: requiredPerUnit,
        min: baseRequiredCount,
        actual: serialNumbers.length
      }));
      return;
    }

    onConfirm(serialNumbers);
  };

  return (
    <Modal
      title={t('operator.qc.serial_number_modal.title', { defaultValue: '掃描序號' })}
      open={open}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText={t('common.button.confirm', { defaultValue: '確認' })}
      cancelText={t('common.button.cancel', { defaultValue: '取消' })}
      width={520}
      centered
      destroyOnHidden
      // antd Modal 開啟動畫 ~300ms。hook 內 mount-time 100ms focus 會在動畫結束前
      // 被 antd 內建的 autoFocusButton="ok" 蓋掉。afterOpenChange 在動畫完成後才觸發，
      // 此時再 focus input 才能贏過 OK 按鈕的 auto-focus。
      afterOpenChange={(visible) => {
        if (visible) serialInputRef.current?.focus();
      }}
    >
      <div style={{ marginBottom: '16px' }}>
        {item && (
          <div>
            <Text strong style={{ fontSize: '16px' }}>
              {item.product?.name || '--'}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('operator.qc.serial_number_modal.sku', { defaultValue: 'SKU' })}: {item.product?.sku || '--'}
            </Text>
          </div>
        )}
      </div>

      <CountInfo completed={isCompleted}>
        <CountLabel>
          {currentCount > baseRequiredCount && completedUnits > baseUnits
            ? t('operator.qc.serial_number_modal.count_label_extended', {
                defaultValue: '已掃描序號數量（{{units}} 件 × {{per_unit}}）',
                units: completedUnits || Math.ceil(currentCount / requiredPerUnit),
                per_unit: requiredPerUnit
              })
            : t('operator.qc.serial_number_modal.count_label', { defaultValue: '已掃描序號數量' })}
        </CountLabel>
        <CountValue completed={isCompleted}>
          {currentCount} / {baseRequiredCount}
          {maxAllowedSerials > baseRequiredCount && (
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 6 }}>
              ({t('operator.qc.serial_number_modal.max_hint', {
                defaultValue: '最多 {{max}}',
                max: maxAllowedSerials
              })})
            </Text>
          )}
        </CountValue>
      </CountInfo>

      {serialError && (
        <Alert
          type="error"
          message={serialError}
          showIcon
          style={{ marginBottom: 8 }}
        />
      )}

      <div>
        <Text style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
          {t('operator.qc.serial_number_modal.scanned_serials', { defaultValue: '已掃描序號' })}
        </Text>
        <SerialNumbersContainer>
          {serialNumbers.length === 0 ? (
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('operator.qc.serial_number_modal.no_serials', { defaultValue: '尚未掃描序號，請在下方輸入框掃描' })}
            </Text>
          ) : (
            <Space size={[8, 8]} wrap>
              {serialNumbers.map((serialNumber, index) => (
                <SerialTag
                  key={index}
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    removeSerialAt(index);
                    serialInputRef.current?.focus();
                  }}
                  color="blue"
                >
                  {serialNumber}
                </SerialTag>
              ))}
            </Space>
          )}
        </SerialNumbersContainer>
      </div>

      <InputWrapper>
        <Text style={{ fontWeight: 500 }}>
          {t('operator.qc.serial_number_modal.input_label', { defaultValue: '掃描序號' })}
        </Text>
        <Input
          ref={serialInputRef}
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          onPressEnter={() => submitSerial()}
          placeholder={t('operator.qc.serial_number_modal.input_placeholder', {
            defaultValue: '請掃描或輸入序號後按 Enter'
          })}
          size="large"
          style={{ fontFamily: 'monospace' }}
          disabled={isCompleted}
        />
        {isCompleted && (
          <Text type="success" style={{ fontSize: '12px' }}>
            {t('operator.qc.serial_number_modal.completed', { defaultValue: '✓ 已完成所需序號掃描' })}
          </Text>
        )}
      </InputWrapper>
    </Modal>
  );
}
